import { useCookies } from '@vueuse/integrations/useCookies';
import UserStore from '@/service/auth/UserService';
import { useMaintenanceModeStore } from '@/stores/maintenanceMode';

const { get } = useCookies(['cookie-name'], { doNotParse: false, autoUpdateDependencies: false });
import router from '@/router';

// const baseURL = 'https://portal-api.legalclaimassistant.support/v1/';
// const baseURL = 'http://127.0.0.1:8000/v1/';
//get from env
const baseURL = import.meta.env.VITE_APP_API_URL;

export const apiRequest = {
    get: await request('GET'),
    post: await request('POST'),
    put: await request('PUT'),
    delete: await request('DELETE')
};

async function request(method) {
    return async (url, body, methodFormData = false, config = {}) => {
        try {
            const requestOptions = {
                method,
                headers: {
                    ...authHeader(),
                    Accept: 'application/json'
                },
                ...config
            };
            if ((method === 'GET' || method === 'HEAD') && body !== undefined) {
                const params = new URLSearchParams();
                const flattenObject = (obj, prefix = '') => {
                    for (const key in obj) {
                        if (Object.prototype.hasOwnProperty.call(obj, key)) {
                            const propName = prefix ? `${prefix}[${key}]` : key;
                            if (typeof obj[key] === 'object') {
                                flattenObject(obj[key], propName);
                            } else {
                                params.append(propName, obj[key]);
                            }
                        }
                    }
                };
                flattenObject(body);
                url += `?${params.toString()}`;
            } else if (body) {
                if (!methodFormData) {
                    requestOptions.headers['Content-Type'] = 'application/json';
                    requestOptions.body = JSON.stringify(body);
                } else {
                    if (url.includes('dashboard/invoices/new') || url.includes('dashboard/invoices/generate-preview') || url.includes('dashboard/creative-upload/create') || url.includes('dashboard/creative-upload/update/')) {
                        requestOptions.body = body;
                    } else {
                        requestOptions.body = appendToFormData(body);
                    }
                }
                // console.log('requestOptions', requestOptions);
            }
            const response = await fetch(baseURL + url, requestOptions);
            return handleResponse(response, config.responseType);
        } catch (error) {
            return Promise.reject(error);
        }
    };
}

function authHeader() {
    const token = get('token');
    if (token) {
        return { Authorization: `Bearer ${token}` };
    }
    return {};
}

async function handleResponse(response, responseType) {
    const UserService = UserStore();

    if (!response.ok) {
        // Laravel maintenance mode: 503
        if (response.status === 503) {
            const maintenanceStore = useMaintenanceModeStore();
            let message = 'The application is currently undergoing scheduled maintenance.';
            try {
                const text = await response.text();
                if (text) {
                    try {
                        const data = JSON.parse(text);
                        message = data.message || data.error || message;
                    } catch (_) {
                        // Not JSON; optional: use first line of HTML/text as message
                        const trimmed = text.trim();
                        if (trimmed.length > 0 && trimmed.length < 500) message = trimmed;
                    }
                }
            } catch (_) {
                // ignore
            }
            maintenanceStore.setMaintenanceMode(message);
            return { status: 'error', message, maintenance: true };
        }

        if ([401, 403].includes(response.status) && UserService.isLoggedIn) {
            UserService.logOut();
            router.push('/auth/login?returnUrl=' + (router.currentRoute.value.path ?? '/'));
        }

        // Try to parse JSON error response (for validation errors, etc.)
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
            try {
                const errorData = await response.json();
                return { status: 'error', message: errorData.message || errorData.errors || 'An error occurred', errors: errorData.errors };
            } catch (e) {
                // If JSON parsing fails, fall back to text
                const error = await response.text();
                return { status: 'error', message: error || 'Bad Request' };
            }
        } else {
            const error = await response.text();
            return { status: 'error', message: error || 'Bad Request' };
        }
    }

    // App is reachable; clear maintenance mode if it was set
    const maintenanceStore = useMaintenanceModeStore();
    if (maintenanceStore.isMaintenanceMode) {
        maintenanceStore.clearMaintenanceMode();
    }

    if (responseType === 'blob') {
        return response.blob();
    }

    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
        const responseData = await response.json();
        // console.log('res', responseData);
        return responseData;
    } else {
        // For non-JSON responses, return the raw response
        return response;
    }
}

async function appendToFormData(obj, parentKey = null) {
    const formData = new FormData();
    for (const key of Object.keys(obj)) {
        const value = obj[key];
        const keyName = parentKey ? `${parentKey}[${key}]` : key;
        if (value instanceof File) {
            formData.append(keyName, value);
        } else if (value instanceof Object) {
            appendToFormData(value, keyName);
        } else {
            formData.append(keyName, value);
        }
    }
    return formData;
}

export default apiRequest;
